import { Box, Grid, Stack, ThemeProvider, Typography } from '@mui/material';
import { gsap } from "gsap";
import * as React from 'react';
import { useRef } from 'react';
import { generateTheme } from '../themes';
import { RPMImage } from './subComponents/RPMImage';
import { RPMText } from './subComponents/RPMText';

const SLICE_MARGIN_MAP = {
    'None':        { xs: 0, sm: 0, md: 0 },
    'Small':       { xs: 2, sm: 2, md: 3 },
    'Normal':      { xs: 6, sm: 8, md: 12 },
    'Large':       { xs: 8, sm: 10, md: 14 },
    'Extra Large': { xs: 10, sm: 12, md: 16 }
};

const AUTO_MARGINS = {
    top: {
        'Small':       { xs: 3, sm: 3, md: 4 },
        'Normal':      { xs: 6, sm: 8, md: 12 },
        'Extra Large': { xs: 8, sm: 10, md: 16 }
    },
    bottom: {
        'Small':       { xs: '1vw', sm: '1vw', md: '1vw' },
        'Normal':      { xs: '5vw', sm: '5vw', md: '5vw' },
        'Extra Large': { xs: '8vw', sm: '8vw', md: '8vw' }
    }
};

const resolveSliceMargin = (value, side, marginsPreset, bp) => {
    if (value && value !== 'Auto') {
        return (SLICE_MARGIN_MAP[value] || SLICE_MARGIN_MAP.None)[bp];
    }
    const fallback = AUTO_MARGINS[side][marginsPreset] || AUTO_MARGINS[side].Normal;
    return fallback[bp];
};

const SellingPointsSlice = ({ data, index, direction }) => {

    const imageRef = useRef(null);
    const imageSliceRef = useRef(null);
    const containerRef = useRef(null);

    const {
        colorTheme = 'white',
        margins = 'Normal',
        columns = 'auto',
        cardStyle = 'plain',
        iconSize = 'medium',
        eyebrow = '',
        sectionTitle = '',
        sectionSubtitle = '',
        items = [],
        sliceMarginTop = 'Auto',
        sliceMarginTopMobile = 'Auto',
        sliceMarginBottom = 'Auto',
        sliceMarginBottomMobile = 'Auto'
    } = data || {};

    const iconSizeMap = {
        small:  {
            panelWidth: { xs: '100%', sm: 90 },  panelMinHeight: { xs: 90,  sm: 90 },  iconMaxWidth: { xs: 50, sm: 50 },
            inlineWidth: 32, topRightWidth: 28,
            barIconHeight: { xs: 22, md: 26 }
        },
        medium: {
            panelWidth: { xs: '100%', sm: 150 }, panelMinHeight: { xs: 130, sm: 150 }, iconMaxWidth: { xs: 90, sm: 90 },
            inlineWidth: 52, topRightWidth: 40,
            barIconHeight: { xs: 30, md: 36 }
        },
        large:  {
            panelWidth: { xs: '100%', sm: 220 }, panelMinHeight: { xs: 160, sm: 180 }, iconMaxWidth: { xs: 130, sm: 150 },
            inlineWidth: 80, topRightWidth: 48,
            barIconHeight: { xs: 40, md: 48 }
        }
    };
    const iconDims = iconSizeMap[iconSize] || iconSizeMap.medium;

    // Dark card backgrounds need light text for contrast
    const DARK_CARDS = ['#0d1a0f', '#16121c', '#4b5563'];
    const isDarkCard = (color) => color && DARK_CARDS.includes(color.toLowerCase());

    // Subtle bright→dark diagonal gradient overlay on top of any base color
    const GRADIENT_OVERLAY = 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(0,0,0,0.12) 100%)';
    const bgWithGradient = (color, useGradient) => {
        if (!color || color === 'transparent') return color;
        return useGradient ? `${GRADIENT_OVERLAY}, ${color}` : color;
    };

    React.useLayoutEffect(() => {
        if (!containerRef.current) return;

        const links = gsap.utils.toArray('.sellingPoint', containerRef.current);

        links.forEach((a, index) => {
            gsap.fromTo(
                a,
                { opacity: 0, y: 10 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.4,
                    delay: 0.1 * index,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: a,
                        start: 'top 92%',
                        markers: false,
                    },
                }
            );
        });
    }, [items.length]);

    const theme = generateTheme(colorTheme);

    const hasSplitItems = items.some(item => item.layout === 'split');

    const autoColumnsFor = (count) => {
        if (count <= 1) return 12;
        if (count === 2) return 6;
        if (count === 4) return 6;
        if (count === 6) return 6;          // 2 cols × 3 rows reads better than 3×2
        if (hasSplitItems) return 6;        // Split layout needs room for text
        return 4;
    };

    const mdWidth = columns === 'auto'
        ? autoColumnsFor(items.length)
        : (columns === '2' ? 6 : columns === '4' ? 3 : 4);

    // Tablet (sm) should always be at most 2 columns — split + 3 cols at sm wraps text word-by-word
    const smWidth = mdWidth === 12 ? 12 : 6;

    const isCard = cardStyle === 'card';
    const baseCardSx = isCard ? {
        backgroundColor: '#FFFEFB',
        borderRadius: '1rem',
        p: { xs: 3, md: 3.5 },
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        height: '100%'
    } : {};

    const itemSxFor = (style) => {
        if (style === 'highlighted') {
            return {
                backgroundColor: '#FFFEFB',
                borderRadius: '1rem',
                p: { xs: 3, md: 3.5 },
                border: '1px solid rgba(15, 105, 120, 0.35)',
                boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                height: '100%'
            };
        }
        if (style === 'dimmed') {
            return {
                backgroundColor: '#F5EFE6',
                borderRadius: '1rem',
                p: { xs: 3, md: 3.5 },
                border: '1px solid rgba(0, 0, 0, 0.08)',
                height: '100%',
                color: 'rgba(0,0,0,0.55)'
            };
        }
        return baseCardSx;
    };

    return (
        <ThemeProvider theme={theme}>
            <Box ref={containerRef} sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                position: "relative",
                background: (theme) => theme.palette.background.default,
                px: { xs: 4, sm: 3, md: 12 },
                pt: {
                    xs: resolveSliceMargin(sliceMarginTopMobile, 'top', margins, 'xs'),
                    sm: resolveSliceMargin(sliceMarginTopMobile, 'top', margins, 'sm'),
                    md: resolveSliceMargin(sliceMarginTop, 'top', margins, 'md')
                },
                pb: {
                    xs: resolveSliceMargin(sliceMarginBottomMobile, 'bottom', margins, 'xs'),
                    sm: resolveSliceMargin(sliceMarginBottomMobile, 'bottom', margins, 'sm'),
                    md: resolveSliceMargin(sliceMarginBottom, 'bottom', margins, 'md')
                }
            }}>
                {(eyebrow || sectionTitle || sectionSubtitle) && (
                    <Box sx={{ mb: { xs: 4, md: 6 }, maxWidth: { xs: "100%", md: "70%" } }}>
                        {eyebrow && (
                            <Typography
                                component="span"
                                sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                    letterSpacing: '0.2em',
                                    textTransform: 'uppercase',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    opacity: 0.7,
                                    mb: { xs: 2, md: 2.5 }
                                }}
                                dangerouslySetInnerHTML={{ __html: eyebrow }}
                            />
                        )}
                        {sectionTitle && <RPMText content={sectionTitle} variant="h2Bold" elementRenderType="h2" />}
                        {sectionSubtitle && <RPMText content={sectionSubtitle} variant="p" />}
                    </Box>
                )}
                <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
                    {items.map((item, index) => {
                        const itemStyle = item.itemStyle || 'default';
                        const isTopRight = item.imagePosition === 'top-right';
                        const isSplit = item.layout === 'split';
                        const baseItemSx = itemSxFor(itemStyle);
                        const darkCard = isDarkCard(item.cardColor);
                        const cardBg = item.cardColor && item.cardGradient
                            ? { background: bgWithGradient(item.cardColor, true), backgroundColor: undefined }
                            : (item.cardColor ? { backgroundColor: item.cardColor } : {});
                        const itemSx = {
                            ...baseItemSx,
                            ...cardBg,
                            ...(darkCard ? {
                                '& .MuiTypography-root': { color: 'rgba(255,255,255,0.95)' },
                                '& h1, & h2, & h3, & h4, & h5, & h6, & p, & span, & li': { color: 'rgba(255,255,255,0.95)' }
                            } : {})
                        };

                        // Split layout + top-right image position → top bar with eyebrow + icon
                        if (isSplit && isTopRight) {
                            return (
                                <Grid className="sellingPoint" item xs={12} sm={smWidth} md={mdWidth} key={index} sx={{ display: 'flex' }}>
                                    <Box sx={{ ...itemSx, width: '100%', p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{
                                            background: bgWithGradient(item.panelColor || '#0A7C85', item.panelGradient),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            px: { xs: 3, md: 3.5 },
                                            py: { xs: 0.75, md: 1 }
                                        }}>
                                            {item.eyebrow ? (
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        display: 'block',
                                                        color: 'rgba(255,255,255,0.95)',
                                                        letterSpacing: '0.18em',
                                                        textTransform: 'uppercase',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 600,
                                                        flex: 1,
                                                        minWidth: 0
                                                    }}
                                                    dangerouslySetInnerHTML={{ __html: item.eyebrow }}
                                                />
                                            ) : <Box sx={{ flex: 1 }} />}
                                            {item.icon && (
                                                <Box
                                                    component="img"
                                                    src={item.icon.url}
                                                    alt={item.icon.alt || ''}
                                                    loading="lazy"
                                                    sx={{
                                                        display: 'block',
                                                        height: iconDims.barIconHeight,
                                                        width: 'auto',
                                                        maxWidth: iconDims.iconMaxWidth,
                                                        objectFit: 'contain',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Box sx={{ flex: 1, p: { xs: 3, md: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                                            <RPMText content={item.title} variant="h4" elementRenderType="h3" />
                                            <RPMText content={item.description} variant="p" elementRenderType="p" />
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        }

                        if (isSplit) {
                            return (
                                <Grid className="sellingPoint" item xs={12} sm={smWidth} md={mdWidth} key={index} sx={{ display: 'flex' }}>
                                    <Box sx={{ ...itemSx, width: '100%', p: 0, overflow: 'hidden', display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                                        <Box sx={{
                                            background: bgWithGradient(item.panelColor || '#0A7C85', item.panelGradient),
                                            width: iconDims.panelWidth,
                                            minHeight: iconDims.panelMinHeight,
                                            flexShrink: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            p: { xs: 3, md: 3.5 }
                                        }}>
                                            {item.icon && <RPMImage image={item.icon} width="100%" height="auto" sx={{ maxWidth: iconDims.iconMaxWidth }} />}
                                        </Box>
                                        <Box sx={{ flex: 1, p: { xs: 3, md: 3.5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                            {item.eyebrow && (
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        display: 'block',
                                                        color: itemStyle === 'highlighted' ? '#0f6978' : 'text.secondary',
                                                        letterSpacing: '0.2em',
                                                        textTransform: 'uppercase',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 600,
                                                        opacity: itemStyle === 'dimmed' ? 0.6 : 0.85,
                                                        mb: { xs: 1.5, md: 2 }
                                                    }}
                                                    dangerouslySetInnerHTML={{ __html: item.eyebrow }}
                                                />
                                            )}
                                            <RPMText content={item.title} variant="h4" elementRenderType="h3" />
                                            <RPMText content={item.description} variant="p" elementRenderType="p" />
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        }
                        return (
                            <Grid className="sellingPoint" item xs={12} sm={mdWidth === 3 ? 6 : mdWidth} md={mdWidth} key={index} sx={{ display: 'flex' }}>
                                <Box sx={{ ...itemSx, width: '100%', position: 'relative' }}>
                                    {isTopRight && item.icon && (
                                        <Box sx={{ position: 'absolute', top: { xs: 16, md: 20 }, right: { xs: 16, md: 20 } }}>
                                            <RPMImage image={item.icon} width={`${iconDims.topRightWidth}px`} height="auto" />
                                        </Box>
                                    )}
                                    {item.eyebrow && (
                                        <Typography
                                            component="span"
                                            sx={{
                                                display: 'block',
                                                color: itemStyle === 'highlighted' ? '#0f6978' : 'text.secondary',
                                                letterSpacing: '0.2em',
                                                textTransform: 'uppercase',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                opacity: itemStyle === 'dimmed' ? 0.6 : 0.85,
                                                mb: { xs: 1.5, md: 2 },
                                                pr: isTopRight ? { xs: 6, md: 7 } : 0
                                            }}
                                            dangerouslySetInnerHTML={{ __html: item.eyebrow }}
                                        />
                                    )}
                                    <Stack direction="row" spacing={0} alignItems="center" sx={{ mb: { xs: 1, md: 1.5 } }}>
                                        {!isTopRight && item.icon && <RPMImage image={item.icon} width={`${iconDims.inlineWidth}px`} height="auto" sx={{ mr: 1.5 }} />}
                                        <Box sx={{ pr: isTopRight && !item.eyebrow ? { xs: 6, md: 7 } : 0 }}>
                                            <RPMText content={item.title} variant="h4" elementRenderType="h3" />
                                        </Box>
                                    </Stack>
                                    <RPMText content={item.description} variant="p" elementRenderType="p" />
                                </Box>
                            </Grid>
                        )
                    })}
                </Grid>
            </Box>

        </ThemeProvider>
    )

}

export default SellingPointsSlice